import apiClient from './apiClient';
import { API_URL } from '../config';

interface BackendTranscription {
  id: number;
  text: string;
  created_at: string;
  updated_at?: string;
  session_id?: string;
  corrected_text?: string;
  status?: string;
  duration_seconds?: number;
}

const mapTranscription = (item: BackendTranscription) => ({
  id: item.id,
  text: item.text,
  createdAt: item.created_at,
  updatedAt: item.updated_at,
  sessionId: item.session_id,
  correctedText: item.corrected_text,
  status: item.status,
  durationSeconds: item.duration_seconds,
});

const getTranscriptionHistory = async () => {
  const response = await apiClient.get(`${API_URL}/transcriptions`);
  return response.data.map(mapTranscription);
};

const getTranscriptionById = async (id: number) => {
  const response = await apiClient.get(`${API_URL}/transcriptions/${id}`);
  return mapTranscription(response.data);
};

const saveTranscription = async (
  text: string,
  sessionId: string | null,
  cptCode?: string | null,
  cptExamName?: string | null,
  correctedText?: string | null,
  cptLaterality?: string | null,
  cptContrast?: string | null,
) => {
  const response = await apiClient.post(`${API_URL}/transcriptions`, {
    text,
    session_id: sessionId,
    corrected_text: correctedText || undefined,
    cpt_code: cptCode || undefined,
    cpt_exam_name: cptExamName || undefined,
    cpt_laterality: cptLaterality || undefined,
    cpt_contrast: cptContrast || undefined,
  });
  return mapTranscription(response.data);
};

const updateTranscription = async (id: number, text: string) => {
  const response = await apiClient.put(`${API_URL}/transcriptions/${id}`, {
    text,
  });
  return mapTranscription(response.data);
};

const deleteTranscription = async (id: number) => {
  const response = await apiClient.delete(`${API_URL}/transcriptions/${id}`);
  return response.data;
};

// --- Session retrieval ---

interface AudioRecording {
  id: number;
  session_id: string;
  file_path: string;
  duration_seconds: number;
  sample_rate: number;
  file_size_bytes: number;
  status: string;
  created_at: string;
  completed_at: string | null;
}

interface SessionSummary {
  session_id: string;
  transcription_id: number;
  text: string;
  corrected_text: string | null;
  status: string;
  duration_seconds: number;
  created_at: string;
  audio: AudioRecording | null;
}

interface TranscriptSegment {
  id: number;
  text: string;
  created_at: string;
}

interface TranscriptDetail {
  session_id: string;
  transcription_id: number;
  text: string;
  corrected_text: string | null;
  status: string;
  created_at: string;
  segments: TranscriptSegment[];
}

const getSessionSummary = async (sessionId: string): Promise<SessionSummary> => {
  const response = await apiClient.get(`${API_URL}/sessions/${sessionId}`);
  return response.data;
};

const getSessionTranscript = async (sessionId: string): Promise<TranscriptDetail> => {
  const response = await apiClient.get(`${API_URL}/sessions/${sessionId}/transcript`);
  return response.data;
};

const getSessionAudioUrl = (sessionId: string): string => {
  return `${API_URL}/sessions/${sessionId}/audio`;
};

export interface AudioRecordingItem {
  id: number;
  session_id: string;
  duration_seconds: number;
  file_size_bytes: number;
  sample_rate: number;
  status: string;
  created_at: string;
  completed_at: string | null;
}

const getSessionRecordings = async (sessionId: string): Promise<AudioRecordingItem[]> => {
  const response = await apiClient.get(`${API_URL}/sessions/${sessionId}/recordings`);
  return response.data;
};

const getRecordingAudioUrl = (sessionId: string, recordingId: number): string => {
  return `${API_URL}/sessions/${sessionId}/recordings/${recordingId}/audio`;
};

// --- Session History ---

interface SessionHistoryItem {
  session_id: string;
  transcription_id: number;
  status: string;
  diagnosis_category: string | null;
  pathology_type: string | null;
  classification_confidence: number | null;
  duration_seconds: number;
  report_status: 'available' | 'not_generated';
  // Report lifecycle fields for immutability enforcement
  report_id: number | null;
  report_lifecycle_status: 'draft' | 'review' | 'final' | null;
  report_finalized_at: string | null;
  report_finalized_by: number | null;
  created_at: string;
  // Patient information
  patient_mrn: string | null;
  patient_name: string | null;
  // CPT exam information (from report)
  cpt_code: string | null;
  cpt_exam_name: string | null;
}

interface SessionHistoryResponse {
  items: SessionHistoryItem[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

interface SessionHistoryParams {
  page?: number;
  page_size?: number;
  diagnosis_category?: string;
  cpt_search?: string;
  search?: string;
}

interface GradingGrade {
  grading_system: string;
  grading_type: string;
  grade: string;
  grade_label: string;
  confidence: string;
  extraction_confidence: string;
  status: string;
  reason: string;
  required_findings_present: number;
  required_findings_total: number;
  missing_findings: string[];
  measurement_validated: boolean;
  start_pos: number;
  end_pos: number;
  raw_match: string;
}

interface GradingData {
  enabled: boolean;
  enforcement_mode: string;
  confidence_threshold: string;
  applicable_systems: string[];
  extracted_count: number;
  kept_grades: GradingGrade[];
  removed_grades: GradingGrade[];
  flags: string[];
}

interface ReportData {
  id: number;
  session_id: string;
  template_id: string;
  report_text: string;
  diagnosis_category: string | null;
  pathology_type: string | null;
  cpt_code: string | null;
  cpt_exam_name: string | null;
  cpt_laterality: string | null;
  cpt_contrast: string | null;
  cpt_detection_method: string | null;
  cpt_detection_confidence: string | null;
  status: string;
  finalized_at: string | null;
  finalized_by: number | null;
  last_edited_at: string | null;
  last_edited_by: number | null;
  created_at: string;
  grading_data?: GradingData | null;
}

interface SessionDetail {
  session_id: string;
  transcription_id: number;
  text: string;
  corrected_text: string | null;
  status: string;
  duration_seconds: number;
  diagnosis_category: string | null;
  pathology_type: string | null;
  classification_confidence: number | null;
  is_user_edited: boolean;
  edited_at: string | null;
  created_at: string;
  audio: AudioRecording | null;
  report: ReportData | null;
  // CPT exam fields (from latest report if available)
  cpt_code?: string | null;
  cpt_exam_name?: string | null;
  cpt_laterality?: string | null;
  cpt_contrast?: string | null;
  cpt_detection_method?: string | null;
  cpt_detection_confidence?: string | null;
}

const getSessionHistory = async (params: SessionHistoryParams = {}): Promise<SessionHistoryResponse> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set('page', String(params.page));
  if (params.page_size) queryParams.set('page_size', String(params.page_size));
  if (params.diagnosis_category) queryParams.set('diagnosis_category', params.diagnosis_category);
  if (params.cpt_search) queryParams.set('cpt_search', params.cpt_search);
  if (params.search) queryParams.set('search', params.search);
  const qs = queryParams.toString();
  const response = await apiClient.get(`${API_URL}/sessions/history${qs ? `?${qs}` : ''}`);
  return response.data;
};

const getSessionDetail = async (sessionId: string): Promise<SessionDetail> => {
  const response = await apiClient.get(`${API_URL}/sessions/${sessionId}/detail`);
  return response.data;
};

interface DiagnosisUpdate {
  diagnosis_category: string;
  pathology_type: string;
}

// --- Transcript editing ---

interface TranscriptEditPayload {
  corrected_text: string;
  user_id?: number;
}

const updateSessionTranscript = async (sessionId: string, data: TranscriptEditPayload): Promise<SessionDetail> => {
  const response = await apiClient.put(`${API_URL}/sessions/${sessionId}/transcript`, data);
  return response.data;
};

interface TranscriptVersionItem {
  id: number;
  session_id: string;
  transcript_text: string;
  source: string;
  created_by: number | null;
  created_at: string;
}

const getTranscriptVersions = async (sessionId: string): Promise<TranscriptVersionItem[]> => {
  const response = await apiClient.get(`${API_URL}/sessions/${sessionId}/versions`);
  return response.data;
};

const updateDiagnosis = async (sessionId: string, data: DiagnosisUpdate) => {
  // Find the transcription by session_id, then update it
  const history = await getTranscriptionHistory();
  const match = history.find((t: { sessionId?: string }) => t.sessionId === sessionId);
  if (!match) throw new Error(`No transcription found for session ${sessionId}`);
  const response = await apiClient.put(`${API_URL}/transcriptions/${match.id}`, data);
  return mapTranscription(response.data);
};

const transcriptionService = {
  getTranscriptionHistory,
  getTranscriptionById,
  saveTranscription,
  updateTranscription,
  deleteTranscription,
  getSessionSummary,
  getSessionTranscript,
  getSessionAudioUrl,
  getSessionRecordings,
  getRecordingAudioUrl,
  updateDiagnosis,
  getSessionHistory,
  getSessionDetail,
  updateSessionTranscript,
  getTranscriptVersions,
};

export type {
  SessionSummary,
  TranscriptDetail,
  TranscriptSegment,
  AudioRecording,
  SessionHistoryItem,
  SessionHistoryResponse,
  SessionHistoryParams,
  SessionDetail,
  ReportData,
  GradingData,
  GradingGrade,
  TranscriptEditPayload,
  TranscriptVersionItem,
};
export default transcriptionService;